import type { ReactNode } from "react";

interface SectionFrameProps {
  children: ReactNode;
  startColor: string;
  endColor: string;
  flipped?: boolean;
  className?: string;
  contentClassName?: string;
  showTopBorder?: boolean;
  showBottomBorder?: boolean;
  clipOverflowX?: boolean;
}

export function SectionFrame({
  children,
  startColor,
  endColor,
  flipped = false,
  className = "",
  contentClassName = "py-16",
  showTopBorder = true,
  showBottomBorder = true,
  clipOverflowX = true,
}: SectionFrameProps) {
  const flipTransform = flipped ? "scaleX(-1)" : "scaleX(1)";

  return (
    <div
      className={`relative max-w-full ${clipOverflowX ? "overflow-x-hidden" : "overflow-visible"} mb-8 ${className}`}
    >
      {/* Top SVG Border */}
      {showTopBorder && (
        <div className="w-full relative z-10" style={{ transform: flipTransform }}>
          <svg
            width="100%"
            height="100"
            viewBox="0 0 1440 100"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
            className="w-full"
            role="img"
            aria-label="Section border decoration"
            style={{
              width: "100vw",
              minWidth: "100vw",
              height: "calc(100vw / 14.4)", // Proportional height based on 1440:100 aspect ratio
              marginLeft: "calc(-50vw + 50%)",
            }}
          >
            <path
              d="M1092.64 1.89829C1182.25 -1.99053 1415.66 0.791041 1322.97 4.0311C1230.29 7.27119 1218.65 20.6878 1331.73 22.8231C1363.45 23.4223 1401.46 22.6494 1440 21.1834V99.9999H0V95.6092C67.6648 92.5733 136.747 89.6992 181.564 88.3895C291.962 85.1633 172.352 73.1395 70.5566 77.5575C-31.239 81.9754 102.033 57.0189 268.441 49.7967C434.85 42.5746 558.285 44.2604 447.956 49.0487C337.627 53.837 505.779 58.6687 627.69 53.3778C749.601 48.0869 784.617 52.8264 823.578 49.9618C854.746 47.67 753.744 45.0377 699.347 44.0077L1020.58 30.0653C1083.37 27.3405 902.988 22.2583 866.415 23.8456C829.842 25.4328 1003.04 5.78712 1092.64 1.89829Z"
              style={{ fill: startColor }}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </div>
      )}

      {/* Gradient Background with Content */}
      <div
        className={`relative z-20 w-screen bg-[linear-gradient(to_bottom,var(--start),var(--end))] pl-[calc(50vw-50%)] pr-[calc(50vw-50%)] ${
          showTopBorder ? "-mt-1" : ""
        }`}
        style={{
          marginLeft: "calc(-50vw + 50%)",
          // CSS variables consumed by the Tailwind arbitrary value above
          ["--start" as string]: startColor,
          ["--end" as string]: endColor,
        }}
      >
        <div className={`container mx-auto px-4 max-w-6xl ${contentClassName}`}>{children}</div>
      </div>

      {/* Bottom SVG Border */}
      {showBottomBorder && (
        <div className="w-full relative z-10 -mt-1" style={{ transform: flipTransform }}>
          <svg
            width="100%"
            height="110"
            viewBox="0 0 1440 110"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
            className="w-full"
            role="img"
            aria-label="Section border decoration"
            style={{
              width: "100vw",
              minWidth: "100vw",
              height: "calc(100vw / 13.09)", // Proportional height based on 1440:110 aspect ratio
              marginLeft: "calc(-50vw + 50%)",
            }}
          >
            <path
              d="M1440 23.6833C1401.46 25.1493 1363.45 25.9222 1331.73 25.323C1218.65 23.1877 1230.29 9.77111 1322.97 6.53101C1415.66 3.29092 1182.25 0.509373 1092.64 4.39819C1003.04 8.28702 829.842 27.9327 866.415 26.3455C902.988 24.7582 1083.37 29.8423 1020.58 32.5671L699.347 46.5076C753.744 47.5376 854.747 50.1719 823.578 52.4636C784.617 55.3283 749.601 50.5877 627.69 55.8787C505.779 61.1696 337.627 56.3379 447.956 51.5496C558.285 46.7613 434.85 45.0755 268.441 52.2976C102.033 59.5197 -31.2389 84.4763 70.5566 80.0583C172.352 75.6404 291.962 87.6642 181.564 90.8904C136.747 92.2001 67.6649 101.063 0 110V-0.000244141H1440V23.6833Z"
              style={{ fill: endColor }}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </div>
      )}
    </div>
  );
}
